import { useCallback, useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { Heart, Users, DollarSign, Repeat, Plus } from "lucide-react";
import { toast } from "sonner";
import { useSiteTime } from "../lib/time";

const TEAL = "#0D9488";

function Stat({ Icon, label, value, sub }) {
  return (
    <Card className="border border-slate-200 bg-white rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        <Icon className="w-3 h-3" style={{ color: TEAL }} /> {label}
      </div>
      <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

/**
 * Compact admin-only form to log Venmo donations that landed outside
 * PayPal so the Supporter roster + user badges stay in sync.
 */
function LogVenmoForm({ onLogged }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [amount, setAmount] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!email.includes("@")) { toast.error("Enter a valid donor email."); return; }
    if (!firstName.trim()) { toast.error("Enter a first name."); return; }
    if (!(amt > 0 && amt <= 5000)) { toast.error("Amount must be between $1 and $5,000."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/supporters/log-venmo", {
        email: email.trim(),
        first_name: firstName.trim(),
        amount: amt,
        send_thank_you: sendEmail,
      });
      toast.success(
        `Logged $${amt.toFixed(2)} · ${data.matched ? "user badged" : "no matching account"}${data.email_sent ? " · thank-you sent" : ""}`
      );
      setEmail(""); setFirstName(""); setAmount("");
      onLogged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-teal-200 bg-teal-50/40 rounded-xl p-5 mb-4" data-testid="log-venmo-card">
      <div className="flex items-center gap-2 mb-3">
        <Plus className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>Log a Venmo donation</div>
      </div>
      <p className="text-xs text-slate-600 mb-4 leading-relaxed">
        Venmo payments don't hit our webhooks. Log them here so the Supporter Wall, dashboard chip, and lifetime total update.
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Input
          type="email"
          placeholder="donor@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-10 bg-white border-slate-200 md:col-span-2"
          data-testid="log-venmo-email"
        />
        <Input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="h-10 bg-white border-slate-200"
          data-testid="log-venmo-first-name"
        />
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
          <Input
            type="number"
            min="1"
            max="5000"
            step="0.01"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-10 pl-7 bg-white border-slate-200 tabular-nums"
            data-testid="log-venmo-amount"
          />
        </div>
        <label className="md:col-span-3 flex items-center gap-2 text-xs text-slate-600 select-none">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="w-4 h-4 accent-teal-600"
            data-testid="log-venmo-send-email"
          />
          Auto-send a warm thank-you email to this address
        </label>
        <Button
          type="submit"
          disabled={saving}
          className="h-10 bg-[#0D9488] hover:bg-[#0F766E] text-white uppercase tracking-widest text-xs font-semibold rounded-md"
          data-testid="log-venmo-submit"
        >
          {saving ? "Logging…" : "Log donation"}
        </Button>
      </form>
    </Card>
  );
}

/**
 * Staff-only donation roster — recent donations newest-first plus lifetime
 * totals so an admin can spot momentum and personally thank donors.
 */
export default function AdminSupporters() {
  const { tz, fmtDateTime } = useSiteTime();
  const [data, setData] = useState({ rows: [], totals: { lifetime_usd: 0, supporters: 0, active_subscriptions: 0 } });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/supporters", { params: { limit: 200 } });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Admin"
        title="Supporter Roster"
        subtitle="Recent donations and total lifetime support. Reach out and say thanks."
        icon={<Heart className="w-8 h-8" style={{ color: TEAL }} />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Stat Icon={DollarSign} label="Lifetime raised" value={`$${Number(data.totals.lifetime_usd || 0).toFixed(2)}`} sub="all-time · one-time + recurring" />
        <Stat Icon={Users} label="Total supporters" value={data.totals.supporters ?? 0} sub="unique users" />
        <Stat Icon={Repeat} label="Active monthly" value={data.totals.active_subscriptions ?? 0} sub="recurring PayPal subs" />
      </div>

      <LogVenmoForm onLogged={load} />

      <Card className="border border-slate-200 bg-white rounded-xl overflow-hidden" data-testid="admin-supporters-table">
        <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Recent donations</div>
          <div className="text-[10px] text-slate-400 font-mono-num">{data.rows.length} shown</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">No donations yet — share the /support page.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <tr>
                  <th className="px-5 py-2.5 text-left">When</th>
                  <th className="px-3 py-2.5 text-left">User</th>
                  <th className="px-3 py-2.5 text-left">Payer email (PayPal)</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5 text-left">Kind</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`supporter-row-${r.id}`}>
                    <td className="px-5 py-2.5 tabular-nums text-slate-700 whitespace-nowrap">{r.created_at ? fmtDateTime(r.created_at) : "—"}</td>
                    <td className="px-3 py-2.5 text-slate-800 truncate max-w-[220px]" title={r.user_email}>{r.user_email || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500 truncate max-w-[220px]" title={r.payer_email}>{r.payer_email || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-teal-700">${Number(r.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2.5">
                      {r.kind === "subscription" ? (
                        <span className="font-mono-num text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">Monthly</span>
                      ) : r.kind === "venmo_manual" ? (
                        <span className="font-mono-num text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-700">Venmo</span>
                      ) : (
                        <span className="font-mono-num text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200 text-teal-700">One-time</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </AppLayout>
  );
}
